import * as React from 'react';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { ApiClient } from '../../../lib/ApiClient';
import { UrlHelpers } from '../../../lib/Helpers';
import { useAuthenticated } from '../../auth/useAuthenticated'
import axios from 'axios';
import { getUrl, useDomainNavigate, domain } from '../../../domain';
import { confirmPasswordSchema, passwordSchema } from '../../../lib/PasswordValidation';
import { v4 as uuidv4 } from 'uuid';
import { logErrorGrayLog } from '../../../lib/ClientErrorGrayLogger';

const apiClient = new ApiClient();

export default function ManagePassword() {
    const userAuth = useAuthenticated(apiClient);
    const [openConfirmation, setOpenConfirmation] = React.useState(false);
    const [errors, setErrors] = React.useState([]);

    // Get ReturnUrl from query string
    let returnUrl = getUrl(UrlHelpers.getSafeUrl(new URLSearchParams(window.location.search).get('ReturnUrl')));

    let navigate = useDomainNavigate();
    if (!userAuth.isAuthenticating && !userAuth.isAuthenticated) {
        navigate("/account/login");
    }
    else if (userAuth.isAuthenticated && !userAuth.result.emailConfirmed)
    {
        navigate("/account/error/confirmEmail");
    }

    const handleCloseConfirmation = (event, reason) => {
        if (reason === 'clickaway') {
          return;
        }

        setOpenConfirmation(false);
    };

    const axiosconfig = {
        headers: {
            "Content-Type": "application/json",
            "SubDomain": domain.subDomain
        },
    };

    const initialValues = {
        oldPassword: "",
        newPassword: "",
        confirmNewPassword: ""
    };

    const validationSchema = yup.object({
        oldPassword: yup
            .string('Enter your old password')
            .required('Old Password is required'),
        newPassword: passwordSchema(),
        confirmNewPassword: confirmPasswordSchema("newPassword"),
    });

    const formik = useFormik({
        initialValues: initialValues,
        validationSchema: validationSchema,
        onSubmit: (values) => {
            axios.post(getUrl(`/identity/account/manage/password`), JSON.stringify(values), axiosconfig)
                .then(response => {
                    formik.setSubmitting(false);

                    if (response.status === 200) {
                        setOpenConfirmation(true);
                        if (returnUrl !== null) {
                            window.location = returnUrl;
                        }
                    }
                    else  {
                        setErrors(response);
                    }
                })
                .catch(error => {
                    if (Array.isArray(error.response.data)) {
                        setErrors(error.response.data);                    
                    }
                    else {
                        if (error?.response?.data?.errorMessage) {
                            setErrors([error?.response?.data?.errorMessage])    
                        }
                        else {
                            console.log("Unexpected error", error?.response, error?.response?.data);
                            const clientError = {
                                referenceGuid: uuidv4(),
                                clientError: JSON.stringify(error),
                                clientStacktrace: "N/A ManagePassword.js",
                                url: window?.location?.href
                            };                        

                            logErrorGrayLog(clientError);
                            setErrors(["Unexpected error: Reference " + clientError.referenceGuid]);
                        }
                    }
                    formik.setSubmitting(false);
                });
        },
    });

    return (
        <>
           <Box
                sx={{
                    marginTop: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Typography variant="subtitle1" gutterBottom component="div" sx={{mt:2}}>
                    Change your password.
                </Typography>
                <form onSubmit={formik.handleSubmit}
                >
                    {
                        errors.map((error) => (
                            <Alert sx={{ mt: 2 }} severity="error">{error}</Alert>
                        ))
                    }
                    <TextField sx={{ mt: 2 }}
                        type="password"
                        name="oldPassword"
                        label="Old Password"
                        fullWidth
                        value={formik.values.oldPassword}
                        onChange={formik.handleChange}
                        error={formik.touched.oldPassword && Boolean(formik.errors.oldPassword)}
                        helperText={formik.touched.oldPassword && formik.errors.oldPassword}
                    />
                    <TextField sx={{ mt: 2 }}
                        type="password"
                        name="newPassword"
                        label="New Password"
                        fullWidth
                        value={formik.values.newPassword}
                        onChange={formik.handleChange}
                        error={formik.touched.newPassword && Boolean(formik.errors.newPassword)}
                        helperText={formik.touched.newPassword && formik.errors.newPassword}
                    />
                    <TextField sx={{ mt: 2 }}
                        type="password"
                        name="confirmNewPassword"
                        label="Confirm New Password"
                        fullWidth
                        value={formik.values.confirmNewPassword}
                        onChange={formik.handleChange}
                        error={formik.touched.confirmNewPassword && Boolean(formik.errors.confirmNewPassword)}
                        helperText={formik.touched.confirmNewPassword && formik.errors.confirmNewPassword}
                    />
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={formik.isSubmitting}
                        sx={{ mt: 3, mb: 2 }}
                    >
                        Save
                    </Button>
                </form>
            </Box>
            <Snackbar
                open={openConfirmation}
                autoHideDuration={6000}
                onClose={handleCloseConfirmation}
            >
                <Alert severity="success">
                        Your password was updated successfully.
                </Alert>
            </Snackbar>
        </>
    );
}